import {
  findIntegrationEntry,
  toIntegrationConnection,
} from '@/components/integrations/integration-catalog';
import { useIntegrationsQuery } from '@/hooks/queries/use-integrations-query';
import { usePageTitleStore } from '@/stores/page-title';
import { Box, Center, Link, Spinner, Text } from '@chakra-ui/react';
import { NavArrowLeft } from 'iconoir-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, Navigate, useParams } from 'react-router-dom';

const IntegrationDetailPage = () => {
  const { t } = useTranslation();
  const { platform } = useParams<{ platform: string }>();
  const setTitle = usePageTitleStore((state) => state.setTitle);
  const entry = findIntegrationEntry(platform);

  useEffect(() => {
    setTitle(t('settings.integrations'));
    return () => setTitle('');
  }, [setTitle, t]);

  const isAssistant = entry?.category === 'assistants';
  const { data: fetchedIntegrations, isLoading, isError } = useIntegrationsQuery({
    enabled: !isAssistant,
  });

  if (!entry) {
    return <Navigate to="/settings/integrations" replace />;
  }

  if (!isAssistant && isLoading) {
    return (
      <Center h="60vh">
        <Spinner size="lg" />
      </Center>
    );
  }

  if (!isAssistant && isError) {
    return (
      <Center h="60vh">
        <Text color="red.500">{t('settings.integrationsPage.errorLoading')}</Text>
      </Center>
    );
  }

  const connection = toIntegrationConnection(
    fetchedIntegrations?.find((i) => i.platform === entry.platform)
  );
  const Component = entry.Component;

  return (
    <Box maxW="2xl" mx="auto">
      <Link
        asChild
        color="fg.muted"
        fontSize="sm"
        mb={6}
        display="inline-flex"
        alignItems="center"
        gap={1}
        _hover={{ color: 'fg' }}
      >
        <RouterLink to="/settings/integrations">
          <NavArrowLeft width={16} height={16} />
          {t('settings.integrations')}
        </RouterLink>
      </Link>

      <Box borderWidth="1px" borderColor="border" rounded="xl" bg="bg.panel" p={{ base: 5, md: 6 }}>
        <Component {...connection} variant="detail" />
      </Box>
    </Box>
  );
};

export default IntegrationDetailPage;
